// ./src/components/features/Timeline/TimelineItemEditDrawer/TimelineItemEditDrawer.tsx
//
// Create/edit drawer for an event timeline / run-of-show item. Self-loading in
// edit mode via recordId; context-or-props resolution; no success toast (the
// page highlight is the confirmation). Fields (single per line): name, category,
// phase (before/during/after, auto-suggested from the start vs the event dates),
// start + optional end date/time (in the event's timezone), who is responsible,
// a vendor, a venue, and a rich-text / markdown description. `eventId` is set on
// CREATE only. Mirrors TaskEditDrawer.
import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, Input, Skeleton, SegmentedControl, Group } from "@mantine/core";
import { OpResult } from "@sdflc/api-helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import { TimelineItem, TimelineItemInput, TIMELINE_PHASE, TIMELINE_DESCRIPTION_FORMAT, VENUE_KIND } from "@/utils/api";
import { getFormErrors, formatErrors, moveEndWithStart } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalTimelineItemsContext } from "@/contexts/TimelineItemsContext";
import { AppDrawer, CategorySelect, DateTimeField } from "@/components/generic";

import { ResponsiblePartySelect } from "../../Tasks/ResponsiblePartySelect/ResponsiblePartySelect";
import { VenueSelect } from "../VenueSelect/VenueSelect";
import { PHASE_META, suggestPhase } from "../timelineHelpers";

dayjs.extend(utc);
dayjs.extend(timezone);

// Both editors pull in heavy bundles (CodeMirror / Tiptap) — lazy-load by path.
const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);
const RichTextField = React.lazy(() =>
  import("@/components/generic/RichTextField/RichTextField").then((m) => ({ default: m.RichTextField }))
);

const resolveTz = (tz?: string | null): string => {
  if (tz) return tz;
  try {
    return dayjs.tz.guess();
  } catch {
    return "UTC";
  }
};

// UTC (stored) ⇄ the event-timezone local string the picker edits.
const utcToZoned = (utcStr?: string | null, tz?: string | null): string | null => {
  if (!utcStr) return null;
  const d = dayjs.utc(utcStr);
  if (!d.isValid()) return null;
  return d.tz(resolveTz(tz)).format("YYYY-MM-DD HH:mm:ss");
};

const zonedToUtc = (localStr?: string | null, tz?: string | null): string | null => {
  if (!localStr) return null;
  const d = dayjs.tz(localStr, resolveTz(tz));
  if (!d.isValid()) return null;
  return d.utc().toISOString();
};

export interface TimelineItemEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this item belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  /** IANA timezone of the event; the schedule fields are edited in this zone. */
  eventTimezone?: string | null;
  /** The event's own window (UTC), used to auto-suggest the phase. */
  eventStartsAt?: string | null;
  eventEndsAt?: string | null;
  /** A suggested start (UTC) for a NEW item — the previous item's end (or +1h). */
  defaultStartsAt?: string | null;
  /** A name for a NEW item (the create wizard's one-tap starters, "Doors open"). Ignored when editing. */
  defaultName?: string | null;
  onCreated?: (item: TimelineItem) => void;
  onUpdated?: (item: TimelineItem) => void;
}

interface FormData {
  name: string;
  categoryId: string | null;
  phase: string;
  startsAtLocal: string | null;
  endsAtLocal: string | null;
  responsibleKind: string | null;
  responsibleId: string | null;
  vendorId: string | null;
  venueId: string | null;
  description: string;
  descriptionFormat: string;
}

const KNOWN_FORM_FIELDS = [
  "name",
  "categoryId",
  "phase",
  "startsAt",
  "endsAt",
  "responsibleKind",
  "responsibleId",
  "vendorId",
  "venueId",
  "description",
];

// A new item pre-fills its start from `defaultStartUtc` (the previous item's end,
// so items chain naturally), and derives its phase from that start.
const getInitialFormData = (
  defaultStartUtc?: string | null,
  tz?: string | null,
  evStart?: string | null,
  evEnd?: string | null,
  defaultName?: string | null
): FormData => {
  const startsAtLocal = defaultStartUtc ? utcToZoned(defaultStartUtc, tz) : null;

  return {
    name: defaultName ?? "",
    categoryId: null,
    phase: defaultStartUtc ? suggestPhase(defaultStartUtc, evStart, evEnd) : TIMELINE_PHASE.BEFORE,
    startsAtLocal,
    endsAtLocal: null,
    responsibleKind: null,
    responsibleId: null,
    vendorId: null,
    venueId: null,
    description: "",
    descriptionFormat: TIMELINE_DESCRIPTION_FORMAT.RICH_TEXT,
  };
};

const fromRecord = (record: TimelineItem, tz?: string | null): FormData => ({
  name: record.name || "",
  categoryId: record.categoryId || null,
  phase: record.phase || TIMELINE_PHASE.BEFORE,
  startsAtLocal: utcToZoned(record.startsAt, tz),
  endsAtLocal: utcToZoned(record.endsAt, tz),
  responsibleKind: record.responsibleKind || null,
  responsibleId: record.responsibleId || null,
  vendorId: record.vendorId || null,
  venueId: record.venueId || null,
  description: record.description || "",
  descriptionFormat: record.descriptionFormat || TIMELINE_DESCRIPTION_FORMAT.RICH_TEXT,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const TimelineItemEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={40} radius="sm" />
    <Skeleton height={40} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
  </Stack>
);

export const TimelineItemEditDrawer: React.FC<TimelineItemEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  eventTimezone,
  eventStartsAt,
  eventEndsAt,
  defaultStartsAt,
  defaultName,
  onCreated,
  onUpdated,
}) => {
  const ctx = useOptionalTimelineItemsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  // Client-side "the end must be after the start" error.
  const [endError, setEndError] = useState<string | null>(null);

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // Whether the user has manually chosen a phase (stops auto-suggestion).
  const phaseTouchedRef = useRef(false);

  // ── Self-loading ──
  const [record, setRecord] = useState<TimelineItem | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as TimelineItem);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record
      ? fromRecord(record, eventTimezone)
      : getInitialFormData(defaultStartsAt, eventTimezone, eventStartsAt, eventEndsAt, defaultName);
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    setEndError(null);
    phaseTouchedRef.current = Boolean(record); // don't auto-suggest over a saved phase
    clearErrRef.current?.();
  }, [opened, record, isEditMode, eventTimezone, eventStartsAt, eventEndsAt, defaultStartsAt, defaultName]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Start change: keep the gap to the end (shift the end by the same delta the
  // start moved), and auto-suggest a phase unless the user picked one manually.
  const handleStartChange = useCallback(
    (local: string | null) => {
      setFormData((prev) => {
        const next = { ...prev, startsAtLocal: local };

        if (prev.startsAtLocal && prev.endsAtLocal && local) {
          next.endsAtLocal = moveEndWithStart(prev.startsAtLocal, prev.endsAtLocal, local);
        }

        if (!phaseTouchedRef.current && local) {
          next.phase = suggestPhase(zonedToUtc(local, eventTimezone), eventStartsAt, eventEndsAt);
        }

        return next;
      });
      setEndError(null);
    },
    [eventTimezone, eventStartsAt, eventEndsAt]
  );

  const handlePhaseChange = useCallback((phase: string) => {
    phaseTouchedRef.current = true;
    setFormData((prev) => ({ ...prev, phase }));
  }, []);

  const handleResponsibleChange = useCallback((kind: string | null, id: string | null) => {
    setFormData((prev) => ({ ...prev, responsibleKind: kind, responsibleId: id }));
  }, []);

  const isMarkdown = formData.descriptionFormat === TIMELINE_DESCRIPTION_FORMAT.MARKDOWN;

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);
    setEndError(null);

    const startUtc = zonedToUtc(formData.startsAtLocal, eventTimezone);
    const endUtc = zonedToUtc(formData.endsAtLocal, eventTimezone);

    if (endUtc && startUtc && dayjs.utc(endUtc).isBefore(dayjs.utc(startUtc))) {
      setEndError("The end must be after the start");
      return;
    }

    const submitData: TimelineItemInput = {
      name: formData.name.trim() || null,
      categoryId: formData.categoryId,
      phase: formData.phase,
      startsAt: startUtc,
      endsAt: endUtc,
      responsibleKind: formData.responsibleKind,
      responsibleId: formData.responsibleId,
      vendorId: formData.vendorId,
      venueId: formData.venueId,
      description: formData.description.trim() || null,
      descriptionFormat: formData.descriptionFormat,
    };

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as TimelineItem[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, eventTimezone, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode ? "Edit timeline item" : "Add timeline item";
  const tzLabel = resolveTz(eventTimezone);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <TimelineItemEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label="Name"
            placeholder="What needs to happen?"
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            required
            data-autofocus
            size="md"
          />

          <Input.Wrapper label="Phase" description="When in the event this happens." error={fieldErrors.phase}>
            <SegmentedControl
              fullWidth
              mt={4}
              value={formData.phase}
              onChange={handlePhaseChange}
              data={PHASE_META.map((p) => ({ value: p.value, label: p.label }))}
            />
          </Input.Wrapper>

          <CategorySelect
            group="timeline"
            label="Category"
            description="Group similar work (setup, catering, AV…)."
            value={formData.categoryId}
            onChange={(id) => setField("categoryId", id)}
            error={fieldErrors.categoryId}
            clearable
            size="md"
          />

          <Group grow align="flex-start">
            <DateTimeField
              label="Starts"
              description={`Event timezone (${tzLabel})`}
              value={formData.startsAtLocal}
              onChange={(_name, v) => handleStartChange(v)}
              error={fieldErrors.startsAt}
              required
              clearable
              size="md"
            />
            <DateTimeField
              label="Ends"
              description="Optional"
              value={formData.endsAtLocal}
              onChange={(_name, v) => {
                setField("endsAtLocal", v);
                setEndError(null);
              }}
              notBefore={formData.startsAtLocal}
              error={endError || fieldErrors.endsAt}
              clearable
              size="md"
            />
          </Group>

          <ResponsiblePartySelect
            eventId={eventId || record?.eventId || ""}
            value={{ kind: formData.responsibleKind, id: formData.responsibleId }}
            onChange={handleResponsibleChange}
            fallbackLabel={record?.responsibleLabel}
            label="Who is responsible"
            kindError={fieldErrors.responsibleKind}
            idError={fieldErrors.responsibleId}
          />

          <VenueSelect
            kind={VENUE_KIND.VENDOR}
            label="Vendor"
            description="A service provider involved (optional)."
            value={formData.vendorId}
            onChange={(id) => setField("vendorId", id)}
            fallbackLabel={record?.vendorLabel}
            error={fieldErrors.vendorId}
          />

          <VenueSelect
            kind={VENUE_KIND.VENUE}
            label="Venue"
            description="Where it happens (optional)."
            value={formData.venueId}
            onChange={(id) => setField("venueId", id)}
            fallbackLabel={record?.venueLabel}
            error={fieldErrors.venueId}
          />

          <Input.Wrapper label="Instructions" error={fieldErrors.description}>
            <SegmentedControl
              size="md"
              mb="xs"
              value={formData.descriptionFormat}
              onChange={(v) => setField("descriptionFormat", v)}
              data={[
                { value: TIMELINE_DESCRIPTION_FORMAT.RICH_TEXT, label: "Rich text" },
                { value: TIMELINE_DESCRIPTION_FORMAT.MARKDOWN, label: "Markdown" },
              ]}
            />
            <Suspense fallback={<Skeleton height={160} radius="sm" />}>
              {isMarkdown ? (
                <MarkdownField
                  value={formData.description}
                  onChange={(md) => setField("description", md)}
                  placeholder="What needs to be done — steps, checklist, details…"
                  minHeight={160}
                />
              ) : (
                <RichTextField
                  value={formData.description}
                  onChange={(html) => setField("description", html)}
                  placeholder="What needs to be done — steps, checklist, details…"
                  minHeight={160}
                />
              )}
            </Suspense>
          </Input.Wrapper>
        </Stack>
      )}
    </AppDrawer>
  );
};

export default TimelineItemEditDrawer;
